import type { ApolloError } from '@apollo/client';
import type { ReactNode } from 'react';

import { clearAuthData, useAuth } from '@pesto/features/src/providers/AuthProvider/AuthProvider';
import { useUser } from '@pesto/hooks';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentUserQuery } from '../__generated__/graphql/api';
import { routes } from '../constants/routes';
import { useIsPublicRoute } from '../hooks/useIsPublicRoute';
import { AUTH_BOOTSTRAP_CONTEXT_KEY } from './apolloErrorHandling';

interface AuthManageProviderProps {
  children: ReactNode;
}

const isUnauthorizedError = (error: ApolloError) => {
  if (error.graphQLErrors.some((graphQLError) => graphQLError.message === 'Unauthorized')) {
    return true;
  }

  return error.message.includes('Unauthorized');
};

export const AuthManageProvider = (props: AuthManageProviderProps) => {
  const { children } = props;
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isPublicRoute = useIsPublicRoute();

  const softSignOut = useCallback(() => {
    clearAuthData();
    navigate(routes.login, { replace: true });
  }, [navigate]);

  useUser({
    queryHook: useCurrentUserQuery,
    isAuthenticated: () => !isPublicRoute && isAuthenticated(),
    signOut: softSignOut,
    queryOptions: {
      context: {
        [AUTH_BOOTSTRAP_CONTEXT_KEY]: true,
      },
    },
    shouldSignOutOnError: isUnauthorizedError,
    onError: () => undefined,
  });

  return <>{children}</>;
};
